import { WizardStepProperties } from 'components/types'
import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { CustomColorsRef } from 'types/general'

import IconRender from '../IconRender'

interface CurrencyOption {
  label: string
  value: string
  icon: string
}

const currencies: CurrencyOption[] = [
  { label: 'CAD ($)', value: 'CAD', icon: 'dollarSign' },
  { label: 'USD ($)', value: 'USD', icon: 'dollarSign' },
  { label: 'EUR (€)', value: 'EUR', icon: 'euroSign' },
]

const MoneyInput = ({
  name,
  label,
  value,
  required,
  isFirstInput,
  onChange,
  setIsValid,
}: WizardStepProperties) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isFocused, setIsFocused] = useState(false)
  const [currentValue, setValue] = useState(value)
  const [currency, setCurrency] = useState<CurrencyOption>(currencies[0])

  const handleFocus = () => {
    setIsFocused(!isFocused)
  }

  const handlePriceChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value, type, name } = e.target

    if (/^\d*\.?\d{0,2}$/.test(value)) {
      setValue(value)
      setIsValid?.(required ? value !== '' : true)

      onChange({ value, type, name })
      handleCurrencyChange()
    }
  }

  const handleCurrencyChange = (e?: ChangeEvent<HTMLSelectElement> | null) => {
    if (e?.target) {
      const { name, value, type } = e.target

      const selectedCurrency =
        currencies.find(curr => curr.value === value) || currency

      setCurrency(selectedCurrency)
      return onChange({ name, value, type })
    }

    onChange({ name: 'currency', value: currency.value })
  }

  useEffect(() => {
    if (required && !currentValue) {
      setIsValid?.(false)
    } else {
      setIsValid?.(true)
    }
  }, [value, setIsValid])

  useEffect(() => {
    if (isFirstInput && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isFirstInput])

  return (
    <div>
      <label
        htmlFor={name}
        className="block text-imovSecondaryDark font-bold mb-2 p-2 text-lg sm:text-base"
      >
        {label}
      </label>
      <div
        className={`flex items-center border-2 rounded-md focus:outline-none ${
          isFocused ? 'border-imovPrimaryLight' : 'border-imovGray'
        }`}
      >
        <span className="p-3 sm:p-2">
          <IconRender
            icon={currency.icon}
            color={
              isFocused ? CustomColorsRef.PrimaryLight : CustomColorsRef.Gray
            }
          />
        </span>
        <input
          type="text"
          ref={inputRef}
          id={name}
          name={name}
          value={currentValue as string}
          onChange={handlePriceChange}
          onFocus={handleFocus}
          onBlur={handleFocus}
          placeholder="0.00"
          className="w-full p-3 sm:p-2 text-lg sm:text-base focus:outline-none"
          maxLength={10}
        />
        <select
          id="currency"
          name="currency"
          value={currency.value}
          onChange={handleCurrencyChange}
          className="ml-2 p-3 sm:p-2 border-l-2 border-imovGray focus:outline-none text-lg sm:text-base"
        >
          {currencies.map(curr => (
            <option key={curr.value} value={curr.value}>
              {curr.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  )
}

export default MoneyInput
